import type { Entity } from '@backstage/catalog-model';

/**
 * Annotation carrying additional owners beyond `spec.owner` — stamped by the
 * gitops catalog provider from CODEOWNERS. Comma-separated entity refs when
 * a service has multiple owners.
 */
export const CODEOWNERS_ANNOTATION = 'regask.io/codeowners';

/**
 * Normalize an entity ref to the full lowercase `kind:namespace/name` form
 * so refs from different sources compare equal. Shorthand owners default to
 * the same rules the catalog applies to `spec.owner`: kind `group`,
 * namespace `default` (so `platform` → `group:default/platform`).
 */
export function normalizeEntityRef(ref: string): string {
  let kind = 'group';
  let namespace = 'default';
  let name = ref.trim().toLowerCase();

  const colon = name.indexOf(':');
  if (colon >= 0) {
    kind = name.slice(0, colon);
    name = name.slice(colon + 1);
  }
  const slash = name.indexOf('/');
  if (slash >= 0) {
    namespace = name.slice(0, slash);
    name = name.slice(slash + 1);
  }
  return `${kind}:${namespace}/${name}`;
}

/**
 * All owners of an entity — its `ownedBy` relation targets (derived from
 * `spec.owner`) plus every CODEOWNERS ref — normalized and deduped, with the
 * primary owner (the relation, listed first by the backend) kept first. A repo
 * commonly has several owners and a mix of users and groups, so this is the
 * full set, not just the primary one.
 */
export function ownerRefs(entity: Entity): string[] {
  // `spec.owner` is the primary owner; keep it first. It normally also appears
  // as an `ownedBy` relation, but reading it directly keeps the list correct
  // even when relations aren't hydrated (e.g. a partial fetch).
  const primary =
    typeof (entity.spec as { owner?: unknown } | undefined)?.owner === 'string'
      ? [normalizeEntityRef((entity.spec as { owner: string }).owner)]
      : [];

  const fromRelations = (entity.relations ?? [])
    .filter(relation => relation.type === 'ownedBy')
    .map(relation => normalizeEntityRef(relation.targetRef));

  const codeowners = entity.metadata.annotations?.[CODEOWNERS_ANNOTATION];
  const fromCodeowners = codeowners
    ? codeowners
        .split(',')
        .filter(ref => ref.trim().length > 0)
        .map(normalizeEntityRef)
    : [];

  const seen = new Set<string>();
  const out: string[] = [];
  for (const ref of [...primary, ...fromRelations, ...fromCodeowners]) {
    if (seen.has(ref)) continue;
    seen.add(ref);
    out.push(ref);
  }
  return out;
}

/**
 * Whether `ref` owns the entity, either through a catalog `ownedBy` relation
 * (derived from `spec.owner`) or through the CODEOWNERS annotation. `ref` is
 * matched after normalization, so shorthand and full refs compare equal.
 */
export function isOwnedByRef(entity: Entity, ref: string): boolean {
  const target = normalizeEntityRef(ref);
  return ownerRefs(entity).includes(target);
}

/**
 * Whether one of the user's ownership refs (from
 * `identityApi.getBackstageIdentity().ownershipEntityRefs` — the user plus
 * their groups) owns the entity.
 */
export function isOwnedByUser(
  entity: Entity,
  ownershipEntityRefs: string[],
): boolean {
  return ownershipEntityRefs.some(ref => isOwnedByRef(entity, ref));
}

/** Filter a component list down to the ones the user owns. */
export function ownedComponents(
  entities: Entity[],
  ownershipEntityRefs: string[],
): Entity[] {
  return entities.filter(entity => isOwnedByUser(entity, ownershipEntityRefs));
}

/** Filter a component list down to the ones owned by a single owner ref. */
export function componentsForOwner(entities: Entity[], ref: string): Entity[] {
  return entities.filter(entity => isOwnedByRef(entity, ref));
}

/** One selectable owner in the Home page filter: a ref and how many of the
 * user's components it owns. */
export interface OwnerFilterOption {
  /** Normalized owner ref (e.g. `group:default/platform-team`). */
  ref: string;
  /** How many of `entities` this ref owns. */
  count: number;
}

/**
 * The owners the signed-in user can filter their catalog by: each of their
 * ownership refs (themselves plus their groups) that owns at least one of
 * `entities`, with the owned count. Deduped, input order preserved (identity
 * lists the user ref first), so the caller can surface "Me" before groups.
 */
export function ownerFilterOptions(
  entities: Entity[],
  ownershipEntityRefs: string[],
): OwnerFilterOption[] {
  const seen = new Set<string>();
  const options: OwnerFilterOption[] = [];
  for (const raw of ownershipEntityRefs) {
    const ref = normalizeEntityRef(raw);
    if (seen.has(ref)) continue;
    seen.add(ref);
    const count = componentsForOwner(entities, ref).length;
    if (count > 0) {
      options.push({ ref, count });
    }
  }
  return options;
}
